import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, UserRound } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { SpecificityField } from "@/components/SpecificityField";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function NewCase() {
  const navigate = useNavigate();
  const [employeeId, setEmployeeId] = useState("");
  const [form, setForm] = useState({ feedback_dates: "", gap: "", standard: "", direction_support: "", result: "" });

  const { data: employees } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => (await api.get("/employees")).data,
  });

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const create = useMutation({
    mutationFn: async () => (await api.post("/cases", { employee_id: employeeId, feedback: form })).data,
    onSuccess: (d) => {
      toast.success("Feedback & gap record logged");
      navigate(`/cases/${d.id}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const canSubmit = employeeId && Object.values(form).every((v) => v.trim().length > 0);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-patria-muted hover:text-patria-ink" data-testid="newcase-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <div className="inline-flex items-center gap-2 rounded-full bg-patria-sage/70 px-3 py-1 text-xs font-medium text-patria-greenDeep mb-3">
          <ClipboardCheck className="h-3.5 w-3.5" /> Stage 0 · Before COMPASS
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-patria-ink">Documented Feedback & Gap Record</h1>
        <p className="text-patria-muted mt-1 leading-relaxed">
          This record is the required precondition — a case cannot enter COMPASS without documented feedback that the gap persists.
          Be specific: name the exact gap, the exact standard, and the exact measure, tied to dates.
        </p>
      </div>

      <Card className="bg-patria-surface border-patria-muted/15 p-6 space-y-5">
        <div className="space-y-2">
          <Label className="text-patria-ink font-medium">Employee <span className="text-patria-terracotta">*</span></Label>
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="bg-patria-surface border-patria-muted/30" data-testid="newcase-employee"><SelectValue placeholder="Select a team member" /></SelectTrigger>
            <SelectContent className="bg-patria-surface">
              {employees?.map((e) => (
                <SelectItem key={e.id} value={e.id}>
                  <span className="inline-flex items-center gap-2"><UserRound className="h-3.5 w-3.5 text-patria-muted" />{e.name} · {e.title} · {e.campus}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {employees?.length === 0 && <p className="text-xs text-patria-muted">No employees assigned to you yet.</p>}
        </div>

        <SpecificityField
          label="Date(s) feedback was given"
          value={form.feedback_dates}
          onChange={set("feedback_dates")}
          minLen={3}
          requireDate
          single
          placeholder="e.g., 4/28, 5/12, 5/26"
          helper="When did you give this feedback? Include the actual dates."
          testId="newcase-feedback-dates"
        />

        <SpecificityField
          label="The specific gap (behaviors/results, with evidence & dates)"
          value={form.gap}
          onChange={set("gap")}
          minLen={40}
          requireDate
          placeholder="State the exact behavior or result that fell short, with examples and dates."
          helper='Reject vague impressions. Instead of "poor communication", describe what happened, how often, and when.'
          testId="newcase-gap"
        />

        <SpecificityField
          label="The standard expected (tied to the job description)"
          value={form.standard}
          onChange={set("standard")}
          minLen={25}
          placeholder="What does 'meeting expectations' look like for this role?"
          helper="Tie the standard to the job description — objective, not impression."
          testId="newcase-standard"
        />

        <SpecificityField
          label="Direction & support provided"
          value={form.direction_support}
          onChange={set("direction_support")}
          minLen={25}
          placeholder="What coaching, resources, or reminders did you provide?"
          helper="Document the reasonable opportunity to change that was given."
          testId="newcase-direction"
        />

        <SpecificityField
          label="Result — did the gap change?"
          value={form.result}
          onChange={set("result")}
          minLen={20}
          placeholder="Describe whether the gap improved, and whether it persists."
          helper="COMPASS is only entered when the gap persists after direct feedback."
          testId="newcase-result"
        />

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => navigate(-1)} data-testid="newcase-cancel" className="rounded-full border-patria-muted/30">Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending} data-testid="newcase-submit" className="rounded-full bg-patria-green hover:bg-patria-greenDeep text-patria-surface">
            {create.isPending ? "Saving…" : "Log feedback record"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
